/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import isNil from 'lodash/isNil';
import { withTheme } from 'styled-components';
import StyledProps from '../theme/AppThemeInterface';
import ReactDOM from 'react-dom';
import StyledControlWrapper from './StyledControlWrapper';
import StyledGoogleMapsControl from './StyledGoogleMapsControl';
import size from 'lodash/size';
import { DISPLAY_NONE_CLASS, DISPLAY_BLOCK_CLASS } from './BaseMapWrapper';

const CONTROL_PADDING = '4px';

// see https://developers.google.com/maps/documentation/javascript/controls#CustomPositioning
export enum MapControlOrderIndex {
  TAIL_TAGS_CONTOL,
  OVERLAYS_CONTROL,
  MAP_SETTTINGS_CONTROL,
  MAP_RULER_TOOL_CONTROL,
  ZOOM_CONTROL,
  FULLSCREEN_CONTROL,
  PERFORMANCE_OUTLOOK_LEGEND_CONTOL,
  TERMINALS_IN_BEAM_CONTROL,
}
export interface GoogleMapsControlItem {
  render: () => React.ReactNode;
  onClick: (event: Event) => {};
  id: string;
  isSmallSize: boolean;
  buttonTextColor: string;
  className?: string;
}

export interface GoogleMapsControlProps extends StyledProps {
  map: google.maps.Map;
  mapPosition: google.maps.ControlPosition;
  height: number;
  marginTop?: number;
  items: GoogleMapsControlItem[];
  hidden?: boolean;
  index?: number;
}

export interface GoogleMapsControlState {}

const GoogleMapsControl = ({
  map,
  mapPosition,
  height,
  marginTop,
  items,
  hidden,
  index,
  theme,
}: GoogleMapsControlProps) => {
  const [controlDiv, setControlDiv] = useState<HTMLDivElement | undefined>(undefined);
  const eventListeners = useRef<google.maps.MapsEventListener[]>([]);
  // private controlDiv: HTMLDivElement | undefined;
  // private eventListeners: google.maps.MapsEventListener[] = [];

  const appendItem = useCallback(
    (
      item: GoogleMapsControlItem,
      div: HTMLDivElement,
      isOnlyItem: boolean,
      isFirstItem: boolean,
      isLastItem: boolean,
    ) => {
      const itemDiv = document.createElement('div');
      ReactDOM.render(
        <StyledGoogleMapsControl
          buttonTextColor={theme.secondaryButtonTextColor}
          topBorder={!(isOnlyItem || isFirstItem)}
          bottomBorder={!(isOnlyItem || isLastItem)}
          isSmallSize={item.isSmallSize}
          className={item.className}
          id={item.id}
        >
          {item.render()}
        </StyledGoogleMapsControl>,
        itemDiv,
      );
      div.appendChild(itemDiv);

      // add google event handler
      eventListeners.current.push(google.maps.event.addDomListener(itemDiv, 'click', item.onClick));
    },
    [eventListeners, theme],
  );

  const renderDiv = useCallback(
    (div: HTMLDivElement) => {
      div.style.padding = CONTROL_PADDING;

      const controlWrapper = document.createElement('div');
      let styledWrapperComponent: HTMLDivElement;
      ReactDOM.render(
        <StyledControlWrapper
          ref={(c: HTMLDivElement) => {
            styledWrapperComponent = c;
          }}
          height={height}
          marginTop={marginTop}
          backgroundColor={theme.primaryBackgroundColor}
        />,
        controlWrapper,
        () => {
          // create the child items to the div
          items.forEach((item: GoogleMapsControlItem, index: number) => {
            const isOnlyItem = size(items) === 1;
            const isLastItem = isOnlyItem || items.length - 1 === index;
            const isFirstItem = isOnlyItem || index === 0;

            appendItem(item, styledWrapperComponent, isOnlyItem, isFirstItem, isLastItem);
          });
        },
      );

      // add the control items to the parent control
      div.appendChild(controlWrapper);
    },
    [height, marginTop, theme, appendItem, items],
  );

  useEffect(() => {
    // hide or show the control
    if (controlDiv) {
      if (!hidden) {
        controlDiv.classList.add(DISPLAY_BLOCK_CLASS);
        controlDiv.classList.remove(DISPLAY_NONE_CLASS);
      } else {
        controlDiv.classList.add(DISPLAY_NONE_CLASS);
        controlDiv.classList.remove(DISPLAY_BLOCK_CLASS);
      }
    }

    return () => {
      // hide the current control if unmounting
      if (controlDiv) {
        controlDiv.classList.add(DISPLAY_NONE_CLASS);
        controlDiv.classList.remove(DISPLAY_BLOCK_CLASS);
      }
    };
  }, [controlDiv, hidden]);

  useEffect(() => {
    let eventListenersCleanup: google.maps.MapsEventListener[];
    if (!isNil(map)) {
      const controlDivTemp = document.createElement('div');
      if (!isNil(index)) {
        // @ts-ignore
        controlDivTemp.index = index;
      }
      renderDiv(controlDivTemp);
      const existingControls = map.controls[mapPosition].getArray().slice();
      map.controls[mapPosition].clear();
      existingControls.push(controlDivTemp);
      existingControls.forEach(control => {
        map.controls[mapPosition].push(control);
      });

      setControlDiv(controlDivTemp);

      // save the event listener array that will need to be cleaned up on unmount
      eventListenersCleanup = eventListeners.current;
    }

    return () => {
      if (!isNil(map)) {
        eventListenersCleanup.forEach((listener: google.maps.MapsEventListener) => {
          google.maps.event.removeListener(listener);
        });
      }
    };
  }, [renderDiv, map, mapPosition, index]);

  return null;
};

export default withTheme(GoogleMapsControl);
